/**
 * Import Validation Utilities
 * Validates client data for bulk import functionality
 *
 * Created: 2025-12-07
 * Purpose: Frontend validation for Import Clients feature
 */

/**
 * Validates PAN (Permanent Account Number) format
 * Format: AAAAA0000A (5 letters + 4 digits + 1 letter)
 * 4th character indicates holder type: C=Company, P=Person, H=HUF, F=Firm, etc.
 *
 * @param {string} pan - PAN number to validate
 * @returns {object} { isValid: boolean, error: string|null }
 */
export const validatePAN = (pan) => {
  if (!pan || typeof pan !== 'string') {
    return { isValid: false, error: "PAN is required" };
  }

  const trimmedPAN = pan.trim().toUpperCase();

  if (trimmedPAN.length !== 10) {
    return { isValid: false, error: "PAN must be exactly 10 characters" };
  }

  // PAN format: AAAAA0000A
  // First 5: Letters (A-Z)
  // Next 4: Digits (0-9)
  // Last 1: Letter (A-Z)
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

  if (!panRegex.test(trimmedPAN)) {
    return { isValid: false, error: "Invalid PAN format. Expected: AAAAA0000A (5 letters + 4 digits + 1 letter)" };
  }

  // 4th character validation (entity type)
  const validEntityTypes = ['A', 'B', 'C', 'F', 'G', 'H', 'L', 'J', 'P', 'T', 'K'];
  const entityType = trimmedPAN[3];
  if (!validEntityTypes.includes(entityType)) {
    return { isValid: false, error: `Invalid PAN entity type '${entityType}' at 4th position` };
  }

  return { isValid: true, error: null };
};

/**
 * Validates GSTIN (Goods and Services Tax Identification Number) format.
 *
 * The Indian GST portal issues TWO structurally distinct GSTIN families
 * differentiated by the 14th character:
 *   - Regular taxpayer (14th = 'Z'): embeds PAN (5 letters + 4 digits + 1 letter)
 *       Example: 22AAAAA0000A1Z5
 *   - Tax Deductor / Collector / Non-resident / OIDAR (14th = 'D'|'C'|'N'|'O'):
 *       embeds TAN (4 letters + 5 digits + 1 letter)
 *       Example: 06RTKA24743F1DT (TDS deductor)
 *
 * Both families share state-code, entity-digit, and checksum positions.
 *
 * @param {string} gstin - GSTIN to validate
 * @returns {object} { isValid: boolean, error: string|null }
 */
export const validateGSTIN = (gstin) => {
  if (!gstin || typeof gstin !== 'string') {
    return { isValid: false, error: "GSTIN is required" };
  }

  const trimmedGSTIN = gstin.trim().toUpperCase();

  if (trimmedGSTIN.length !== 15) {
    return { isValid: false, error: "GSTIN must be exactly 15 characters" };
  }

  // Basic format check — alternation covers both GSTIN families. The
  // entity-character (14th) discriminates: 'Z' = regular taxpayer with
  // PAN-style middle, [DCNO] = TDS/TCS/Non-resident/OIDAR with TAN-style
  // middle.
  const gstinRegex = /^(?:[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}|[0-9]{2}[A-Z]{4}[0-9]{5}[A-Z]{1}[1-9A-Z]{1}[DCNO][0-9A-Z]{1})$/;

  if (!gstinRegex.test(trimmedGSTIN)) {
    return { isValid: false, error: "Invalid GSTIN format. Expected: 22AAAAA0000A1Z5 (regular taxpayer) or 22AAAA00000A1DT (TDS/TCS/Non-resident/OIDAR)" };
  }

  // State code validation (01-37, plus special codes)
  const stateCode = parseInt(trimmedGSTIN.substring(0, 2), 10);
  const validStateCodes = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
    21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38,
    97, 99 // Special codes for other territories
  ];

  if (!validStateCodes.includes(stateCode)) {
    return { isValid: false, error: `Invalid state code '${trimmedGSTIN.substring(0, 2)}' in GSTIN` };
  }

  // Validate embedded PAN (characters 3-12) — only for regular taxpayer
  // GSTINs (14th == 'Z'). The other families embed TAN, whose structure
  // is already enforced by the alternation regex above; an additional
  // PAN check would falsely reject them.
  if (trimmedGSTIN[13] === 'Z') {
    const embeddedPAN = trimmedGSTIN.substring(2, 12);
    const panValidation = validatePAN(embeddedPAN);
    if (!panValidation.isValid) {
      return { isValid: false, error: "Invalid PAN embedded in GSTIN" };
    }
  }

  // Checksum validation
  const checksumValid = validateGSTINChecksum(trimmedGSTIN);
  if (!checksumValid) {
    return { isValid: false, error: "Invalid GSTIN checksum" };
  }

  return { isValid: true, error: null };
};

/**
 * Validates GSTIN checksum (15th character)
 * Uses Luhn algorithm variant for GSTIN
 *
 * @param {string} gstin - 15-character GSTIN
 * @returns {boolean} - true if checksum is valid
 */
const validateGSTINChecksum = (gstin) => {
  const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let sum = 0;

  for (let i = 0; i < 14; i++) {
    const charIndex = chars.indexOf(gstin[i]);
    const factor = (i % 2 === 0) ? 1 : 2;
    const product = charIndex * factor;
    sum += Math.floor(product / 36) + (product % 36);
  }

  const remainder = sum % 36;
  const checkChar = chars[(36 - remainder) % 36];

  return gstin[14] === checkChar;
};

/**
 * Validates TAN (Tax Deduction Account Number) format
 * Format: AAAA00000A (4 letters + 5 digits + 1 letter)
 *
 * @param {string} tan - TAN to validate
 * @returns {object} { isValid: boolean, error: string|null }
 */
export const validateTAN = (tan) => {
  if (!tan || typeof tan !== 'string') {
    return { isValid: false, error: "TAN is required" };
  }

  const trimmedTAN = tan.trim().toUpperCase();

  if (trimmedTAN.length !== 10) {
    return { isValid: false, error: "TAN must be exactly 10 characters" };
  }

  // TAN format: AAAA00000A
  // First 4: Letters (A-Z)
  // Next 5: Digits (0-9)
  // Last 1: Letter (A-Z)
  const tanRegex = /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/;

  if (!tanRegex.test(trimmedTAN)) {
    return { isValid: false, error: "Invalid TAN format. Expected: AAAA00000A (4 letters + 5 digits + 1 letter)" };
  }

  return { isValid: true, error: null };
};

/**
 * Validates DOB (Date of Birth) format
 * Accepts: DD/MM/YYYY or DD-MM-YYYY
 *
 * @param {string|number} dob - Date of birth to validate
 * @returns {object} { isValid: boolean, error: string|null, formattedDate: string|null }
 */
export const validateDOB = (dob) => {
  if (!dob) {
    return { isValid: false, error: "DOB is required", formattedDate: null };
  }

  let dateStr = String(dob).trim();

  // Handle Excel serial date numbers
  if (/^\d+$/.test(dateStr) && parseInt(dateStr) > 1000) {
    const excelDate = excelSerialToDate(parseInt(dateStr));
    if (excelDate) {
      return { isValid: true, error: null, formattedDate: excelDate };
    }
    return { isValid: false, error: "Invalid date format", formattedDate: null };
  }

  // Accept DD/MM/YYYY or DD-MM-YYYY
  const dateRegex = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
  const match = dateStr.match(dateRegex);

  if (!match) {
    return { isValid: false, error: "Invalid DOB format. Expected: DD/MM/YYYY or DD-MM-YYYY", formattedDate: null };
  }

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  // Basic range validation
  if (month < 1 || month > 12) {
    return { isValid: false, error: "Invalid month in DOB (must be 01-12)", formattedDate: null };
  }

  if (day < 1 || day > 31) {
    return { isValid: false, error: "Invalid day in DOB (must be 01-31)", formattedDate: null };
  }

  // Year validation (reasonable range)
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear) {
    return { isValid: false, error: `Invalid year in DOB (must be 1900-${currentYear})`, formattedDate: null };
  }

  // Validate actual date
  const testDate = new Date(year, month - 1, day);
  if (testDate.getDate() !== day || testDate.getMonth() !== month - 1 || testDate.getFullYear() !== year) {
    return { isValid: false, error: "Invalid date in DOB", formattedDate: null };
  }

  // Format to DD/MM/YYYY for consistency
  const formattedDate = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;

  return { isValid: true, error: null, formattedDate };
};

/**
 * Converts Excel serial date to DD/MM/YYYY format
 * Excel dates are number of days since 1900-01-01 (with a bug for 1900 leap year)
 *
 * @param {number} serial - Excel serial date number
 * @returns {string|null} - Formatted date or null if invalid
 */
const excelSerialToDate = (serial) => {
  try {
    // Excel incorrectly treats 1900 as a leap year, so we adjust
    const utcDays = Math.floor(serial - 25569);
    const date = new Date(utcDays * 86400 * 1000);

    if (isNaN(date.getTime())) return null;

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return null;
  }
};

/**
 * Validates a single row of import data based on service type
 *
 * @param {Array} row - Array of cell values
 * @param {number} rowIndex - Row number (for error reporting)
 * @param {string} serviceType - 'income-tax', 'tds', or 'gstin'
 * @param {Array} headers - Column headers
 * @returns {object} { isValid: boolean, errors: Array<{column, error}>, formattedRow: Array }
 */
export const validateImportRow = (row, rowIndex, serviceType, headers) => {
  const errors = [];
  const formattedRow = [...row];

  headers.forEach((header, colIndex) => {
    const cellValue = row[colIndex];
    const isEmpty = cellValue === undefined || cellValue === null || String(cellValue).trim() === '';

    // Assigned User Email is optional — empty cell maps to "Shared pool"
    // (NULL assigned_user_id) per USER_LEVEL_CLIENT_ASSIGNMENT.md §6.
    // Backend resolves the email when present and accepts NULL when absent.
    if (header === 'Assigned User Email' && isEmpty) {
      return;
    }

    // Check for empty required fields
    if (isEmpty) {
      errors.push({
        column: header,
        value: '',
        error: `${header} is required`
      });
      return;
    }

    const value = String(cellValue).trim();

    // Validate based on column type (using user-friendly column names)
    switch (header) {
      case 'PAN/TAN': {
        const result = validatePAN(value);
        if (!result.isValid) {
          errors.push({ column: header, value, error: result.error });
        }
        break;
      }

      case 'GSTIN': {
        const result = validateGSTIN(value);
        if (!result.isValid) {
          errors.push({ column: header, value, error: result.error });
        }
        break;
      }

      case 'TAN Number': {
        const result = validateTAN(value);
        if (!result.isValid) {
          errors.push({ column: header, value, error: result.error });
        }
        break;
      }

      case 'DOB': {
        const result = validateDOB(value);
        if (!result.isValid) {
          errors.push({ column: header, value, error: result.error });
        } else if (result.formattedDate) {
          // Store formatted date for upload
          formattedRow[colIndex] = result.formattedDate;
        }
        break;
      }

      // Name and Password fields - just check for empty (already done above)
      case 'Name Of Assessee':
      case 'Client Name':
      case 'Username':
      case 'Password':
        // No additional format validation needed
        break;

      default:
        // Unknown column - skip validation
        break;
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    formattedRow
  };
};

/**
 * Validates all rows in import data
 *
 * @param {Array<Array>} dataRows - Array of row data (excluding header row)
 * @param {string} serviceType - 'income-tax', 'tds', or 'gstin'
 * @param {Array} headers - Column headers
 * @returns {object} { validRows, invalidRows, summary }
 */
export const validateImportData = (dataRows, serviceType, headers) => {
  const validRows = [];
  const invalidRows = [];

  dataRows.forEach((row, index) => {
    const rowNumber = index + 2; // +2 because: +1 for 0-index, +1 for header row
    const validation = validateImportRow(row, rowNumber, serviceType, headers);

    if (validation.isValid) {
      validRows.push({
        rowNumber,
        data: validation.formattedRow
      });
    } else {
      invalidRows.push({
        rowNumber,
        data: row,
        errors: validation.errors
      });
    }
  });

  return {
    validRows,
    invalidRows,
    summary: {
      total: dataRows.length,
      valid: validRows.length,
      invalid: invalidRows.length
    }
  };
};

/**
 * Generates error remarks string for a row
 *
 * @param {Array<{column, error}>} errors - Array of errors for a row
 * @returns {string} - Formatted error remarks
 */
export const generateErrorRemarks = (errors) => {
  return errors.map(e => `${e.column}: ${e.error}`).join('; ');
};

/**
 * Column name for error remarks in downloaded error file
 */
export const ERROR_REMARKS_COLUMN = 'Error Remarks';
